import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Search,
  Upload,
  Download,
  Send,
  Bot,
  Database,
  Code,
  Zap,
  Settings,
  Monitor,
  Shield,
  Tag,
  FileCode,
  Copy,
  RefreshCw,
  Play,
  Eye,
  Edit3,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Wrench,
  Target,
  Book,
  Archive,
  Layers,
  Activity,
  BarChart3,
  Filter,
  Plus,
  Save,
  Info,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Terminal,
  Folder,
  FileX,
  Hash,
  Cpu,
  Gauge,
  Lightbulb,
  BookOpen,
  MessageCircle,
  TrendingUp,
  Briefcase,
  Star
} from 'lucide-react';

const DocumentDrivenPLCPlatform = () => {
  const [activeTab, setActiveTab] = useState('smart-search');
  const [searchQuery, setSearchQuery] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [generatedCode, setGeneratedCode] = useState('');
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState('current');

  // Realistic uploaded documentation database
  const uploadedDocs = [
    {
      id: 1,
      name: 'SIT_Standards_2018.pdf',
      type: 'standards',
      size: '2.1 MB',
      pages: 47,
      uploadDate: '2025-07-22',
      project: 'current',
      processed: true,
      extractedRules: {
        naming: 'UpperCamelCase',
        inputPrefix: 'i',
        outputPrefix: 'q',
        markerPrefix: 'm',
        libraryPrefix: 'lb',
        instancePrefix: 'e',
        customerAbbr: 'Ama'
      },
      keyContent: [
        'Naming conventions for blocks, variables, instances',
        'Programming language guidelines (LAD, SCL, STL)',
        'Object interface definitions',
        'Customer-specific abbreviations'
      ]
    },
    {
      id: 2,
      name: 'ABB_ACS580_Drive_Manual.pdf',
      type: 'equipment',
      size: '15.7 MB',
      pages: 892,
      uploadDate: '2025-07-20',
      project: 'current',
      processed: true,
      equipment: 'VFD',
      manufacturer: 'ABB',
      model: 'ACS580',
      parameterCount: 2847,
      keyContent: [
        '2847 parameters with descriptions',
        'Fault codes F001-F999 with solutions',
        'Commissioning procedures',
        'Communication protocols (Modbus, Ethernet/IP)',
        'Wiring diagrams and specifications'
      ]
    },
    {
      id: 3,
      name: 'Allen_Bradley_5069_IO_Manual.pdf',
      type: 'equipment',
      size: '8.9 MB',
      pages: 234,
      uploadDate: '2025-07-18',
      project: 'current',
      processed: true,
      equipment: 'IO_Module',
      manufacturer: 'Allen-Bradley',
      model: '5069 Compact I/O',
      keyContent: [
        'I/O module specifications',
        'Wiring diagrams for all modules',
        'Configuration procedures',
        'Troubleshooting guide',
        'Safety and installation requirements'
      ]
    },
    {
      id: 4,
      name: 'Conveyor_System_Specs_v3.docx',
      type: 'project',
      size: '4.2 MB',
      uploadDate: '2025-07-15',
      project: 'current',
      processed: true,
      keyContent: [
        'Mechanical specifications for 12 conveyor zones',
        'Motor and drive requirements',
        'Sensor locations and specifications',
        'Safety system requirements',
        'Performance criteria and testing procedures'
      ]
    },
    {
      id: 5,
      name: 'IEC_61131_Programming_Guide.pdf',
      type: 'standards',
      size: '12.3 MB',
      pages: 567,
      uploadDate: '2025-07-10',
      project: 'library',
      processed: true,
      keyContent: [
        'IEC 61131-3 programming languages',
        'Structured text (ST) syntax',
        'Function block (FB) development',
        'Sequential function chart (SFC) guidelines',
        'Best practices and examples'
      ]
    },
    {
      id: 6,
      name: 'Safety_System_Requirements_SIL2.pdf',
      type: 'safety',
      size: '6.8 MB',
      pages: 123,
      uploadDate: '2025-07-08',
      project: 'current',
      processed: true,
      keyContent: [
        'SIL 2 safety requirements',
        'Dual channel safety architecture',
        'Emergency stop system design',
        'Safety function testing procedures',
        'Documentation requirements'
      ]
    }
  ];

  // Smart search results based on uploaded documentation
  const getSearchResults = (query) => {
    const results = [];
    const q = query.toLowerCase();
    
    if (q.includes('parameter') || q.includes('2.01') || q.includes('acceleration')) {
      results.push({
        type: 'parameter',
        title: 'Parameter 2.01 - Acceleration Time 1',
        source: 'ABB ACS580 Drive Manual, Page 156',
        content: 'Acceleration time from 0 to rated frequency. Range: 0.1 - 999.9s, Default: 10.0s, Unit: seconds',
        relevance: 98,
        quickAction: 'Insert Parameter'
      });
    }
    
    if (q.includes('fault') || q.includes('f042') || q.includes('communication')) {
      results.push({
        type: 'fault',
        title: 'Fault Code F042 - Communication Error',
        source: 'ABB ACS580 Drive Manual, Page 743',
        content: 'Fieldbus communication timeout. Check network connection, verify IP settings, ensure proper termination.',
        solution: 'Reset communication module, verify cable continuity, check network switch ports',
        relevance: 95,
        quickAction: 'View Troubleshooting'
      });
    }
    
    if (q.includes('naming') || q.includes('convention') || q.includes('prefix')) {
      results.push({
        type: 'standard',
        title: 'Variable Naming Convention',
        source: 'SIT Standards 2018, Section 1.2.5',
        content: 'Input prefix: "i", Output prefix: "q", Marker prefix: "m". Format: <Prefix><EquipmentMark>_<Name>',
        example: 'iAK04_F201_CircuitBreakerOk, q1021_03_20-M1_DriveSpeed1',
        relevance: 92,
        quickAction: 'Apply to Code'
      });
    }
    
    if (q.includes('wiring') || q.includes('connection') || q.includes('terminal')) {
      results.push({
        type: 'wiring',
        title: 'Digital Input Wiring - 5069-IB16',
        source: 'Allen-Bradley 5069 I/O Manual, Page 67',
        content: 'Source type connection, 24VDC, terminals 0-15. Common terminal for return path.',
        diagram: 'Wiring_Diagram_5069_IB16.png',
        relevance: 89,
        quickAction: 'View Diagram'
      });
    }
    
    if (q.includes('safety') || q.includes('estop') || q.includes('sil')) {
      results.push({
        type: 'safety',
        title: 'Emergency Stop System - SIL 2',
        source: 'Safety System Requirements, Section 3.2',
        content: 'Dual channel architecture required. Response time <500ms. Manual reset after fault cleared.',
        requirements: 'Two independent E-stop circuits, safety relay monitoring, diagnostic coverage >90%',
        relevance: 87,
        quickAction: 'Generate Safety Code'
      });
    }
    
    // Default suggestions when no specific query
    if (!query || query.length < 3) {
      return [
        {
          type: 'suggestion',
          title: 'Recent Searches',
          items: ['Parameter 2.01 acceleration', 'Fault code F042', 'Motor naming convention', 'Safety interlock wiring']
        },
        {
          type: 'suggestion',
          title: 'Popular Lookups',
          items: ['VFD parameters', 'I/O wiring diagrams', 'Fault codes', 'Naming standards']
        }
      ];
    }
    
    return results.sort((a, b) => (b.relevance || 0) - (a.relevance || 0));
  };

  const generateCodeFromDocs = (request) => {
    // This simulates code generation based on uploaded documentation
    if (request.includes('motor') || request.includes('drive')) {
      return `// Motor Control Logic - Generated using SIT Standards 2018
// Equipment: ABB ACS580 VFD per uploaded manual
// Author: Documentation AI Assistant
// Date: ${new Date().toLocaleDateString()}

//===== INPUTS (per SIT naming convention) =====
iConv01_StartPB          : BOOL;  // Start pushbutton
iConv01_StopPB           : BOOL;  // Stop pushbutton  
iConv01_EStopOK          : BOOL;  // E-stop circuit OK
iConv01_DriveReady       : BOOL;  // VFD ready signal
iConv01_SpeedRef         : INT;   // Speed reference (4-20mA)

//===== OUTPUTS (per SIT naming convention) =====
qConv01_DriveEnable      : BOOL;  // Drive enable command
qConv01_DriveRun         : BOOL;  // Drive run command
qConv01_StatusLED        : BOOL;  // Running status indicator
qConv01_FaultLED         : BOOL;  // Fault status indicator
qConv01_SpeedCmd         : INT;   // Speed command to drive

//===== MARKERS (per SIT naming convention) =====
mConv01_RunPermit        : BOOL;  // Internal run permit
mConv01_DriveRunning     : BOOL;  // Drive running status
mConv01_FaultActive      : BOOL;  // Active fault condition

//===== CONSTANTS (from ABB ACS580 manual) =====
cMinSpeedRef            : INT := 6553;   // 4mA = 0% speed
cMaxSpeedRef            : INT := 32767;  // 20mA = 100% speed
cAccelTime              : REAL := 10.0;  // Parameter 2.01 default
cDecelTime              : REAL := 15.0;  // Parameter 2.02 default

//===== MAIN CONTROL LOGIC =====

// Run permit logic (per Safety Requirements SIL 2)
mConv01_RunPermit := iConv01_EStopOK AND 
                     iConv01_DriveReady AND 
                     NOT mConv01_FaultActive;

// Start/Stop logic with seal-in
mConv01_DriveRunning := (iConv01_StartPB OR mConv01_DriveRunning) AND
                        NOT iConv01_StopPB AND
                        mConv01_RunPermit;

// Drive control outputs
qConv01_DriveEnable := mConv01_RunPermit;
qConv01_DriveRun := mConv01_DriveRunning;

// Speed command scaling (per ACS580 manual scaling)
IF mConv01_DriveRunning THEN
    qConv01_SpeedCmd := iConv01_SpeedRef;  // Direct pass-through
ELSE
    qConv01_SpeedCmd := cMinSpeedRef;      // 0% speed when stopped
END_IF;

// Status indication
qConv01_StatusLED := mConv01_DriveRunning;
qConv01_FaultLED := mConv01_FaultActive OR NOT iConv01_EStopOK;

// Fault detection (based on ACS580 fault monitoring)
mConv01_FaultActive := NOT iConv01_DriveReady OR 
                       NOT iConv01_EStopOK;

(*
COMMISSIONING NOTES (from uploaded documentation):
1. Set ACS580 Parameter 2.01 = 10.0s (acceleration time)
2. Set ACS580 Parameter 2.02 = 15.0s (deceleration time)
3. Configure digital inputs per 5069-IB16 wiring diagram
4. Test E-stop circuit per Safety System Requirements
5. Verify speed reference scaling 4-20mA = 0-100%

STANDARDS COMPLIANCE:
- Naming per SIT Standards 2018 Section 1.2
- Safety architecture per SIL 2 requirements
- VFD integration per ABB ACS580 manual
*)`;
    }
    
    return '// Code generation based on your uploaded documentation...';
  };

  const initializeChat = () => {
    setChatMessages([
      {
        id: 1,
        type: 'assistant',
        content: `🤖 **Documentation AI Ready!**

I've processed **${uploadedDocs.length} documents** from your uploads:

**Standards & Guidelines:**
• SIT Standards 2018 - Naming conventions extracted ✓
• IEC 61131 Programming Guide - Language rules loaded ✓

**Equipment Manuals:**
• ABB ACS580 VFD - 2,847 parameters indexed ✓
• Allen-Bradley 5069 I/O - Wiring diagrams processed ✓

**Project Documentation:**
• Conveyor System Specs - Requirements mapped ✓
• Safety Requirements SIL 2 - Standards applied ✓

**I can help with:**
📖 **Smart Lookups** - "What's parameter 2.01?" → instant answer
⚡ **Code Generation** - Uses YOUR naming conventions automatically  
🔧 **Troubleshooting** - "Fault F042" → solution from YOUR manuals
📊 **Standards Enforcement** - Code follows YOUR uploaded standards

What would you like to work on?`,
        timestamp: new Date().toISOString()
      }
    ]);
  };

  useEffect(() => {
    if (chatMessages.length === 0) {
      initializeChat();
    }
  }, []);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: newMessage,
      timestamp: new Date().toISOString()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setIsGenerating(true);

    setTimeout(() => {
      let response = '';
      const query = newMessage.toLowerCase();
      
      if (query.includes('parameter') && query.includes('2.01')) {
        response = `📋 **Parameter 2.01 - Acceleration Time 1**

From your uploaded **ABB ACS580 manual** (Page 156):

**Parameter Details:**
• **Range:** 0.1 - 999.9 seconds
• **Default:** 10.0 seconds  
• **Unit:** Seconds
• **Description:** Time to accelerate from 0 Hz to rated frequency

**Typical Applications:**
• Conveyor systems: 5-15 seconds (smooth starts)
• Pump applications: 10-30 seconds (reduce water hammer)
• Fan applications: 15-60 seconds (prevent belt slip)

**Related Parameters:**
• 2.02 - Deceleration Time 1
• 2.03 - Acceleration Time 2  
• 2.04 - Deceleration Time 2

Would you like me to generate code using this parameter?`;
      } else if (query.includes('fault') && (query.includes('f042') || query.includes('communication'))) {
        response = `🔧 **Fault F042 - Communication Error**

From your **ABB ACS580 manual** (Page 743):

**Fault Description:** Fieldbus communication timeout or interruption

**Immediate Actions:**
1. **Check Physical Connection**
   • Verify Ethernet cable connected to CN1 port
   • Check for cable damage or loose connections
   • Confirm network switch power and status LEDs

2. **Verify Network Settings**
   • IP Address: Check parameter 51.01
   • Subnet Mask: Check parameter 51.02  
   • Gateway: Check parameter 51.03

3. **Communication Diagnostics**
   • Parameter 51.10: Communication status
   • Parameter 51.11: Error counter
   • Parameter 51.12: Last error code

**Quick Reset:** Set parameter 50.01 = 1 to reset communication module

**Prevention:** Enable communication monitoring in parameter 50.05

Need help with network configuration or parameter settings?`;
      } else if (query.includes('generate') && query.includes('motor')) {
        response = `⚡ **Generating Motor Control Code**

Using your uploaded documentation:
• **SIT Standards 2018** for naming conventions
• **ABB ACS580 manual** for VFD integration
• **Safety Requirements** for SIL 2 compliance

**Code Features:**
✓ Variable naming per SIT standards (i/q/m prefixes)
✓ ABB ACS580 parameter references
✓ Safety interlocks per your requirements
✓ Proper commenting and documentation

**Generated Logic Includes:**
• Start/stop with seal-in circuit
• E-stop safety integration
• VFD speed command scaling
• Fault detection and indication
• Status LED control

The code will be displayed in a new window with full documentation.

Ready to generate?`;
        
        // Trigger code generation
        setTimeout(() => {
          setGeneratedCode(generateCodeFromDocs(newMessage));
          setShowCodeModal(true);
        }, 1000);
      } else if (query.includes('naming') || query.includes('convention')) {
        response = `📝 **Naming Conventions from SIT Standards 2018**

**Variable Prefixes (Section 1.2.5):**
• \`i\` - PLC Inputs (signals from process)
• \`q\` - PLC Outputs (signals to process)  
• \`m\` - Markers/Flags (internal variables)
• \`c\` - Constants (local and global)
• \`t\` - Temporary variables
• \`s\` - Static variables

**Format:** \`<Prefix><EquipmentMark>_<Name>\`

**Examples from your standards:**
• \`iAK04_F201_CircuitBreakerOk\` - Input signal
• \`q1021_03_20-M1_DriveSpeed1\` - Output to motor
• \`mConv01_RunPermit\` - Internal permit flag

**Object Prefixes:**
• \`lb\` - Library blocks (function blocks from SIT library)
• \`e\` - Equipment instances (\`e1021_19_XX\`)

**Customer Abbreviation:** \`Ama\` (Amazon) per your standards

Would you like me to apply these conventions to generated code?`;
      } else {
        response = `🤖 **I can help with your uploaded documentation!**

**Try asking:**
• **Parameter Lookups:** "What's parameter 2.01?" or "Show me deceleration settings"
• **Fault Codes:** "How do I fix fault F042?" or "Communication error solutions"  
• **Code Generation:** "Generate motor control logic" or "Create safety interlock code"
• **Standards:** "Show naming conventions" or "What prefix for inputs?"
• **Wiring:** "How do I wire digital inputs?" or "Show terminal connections"

**Available Documentation:**
${uploadedDocs.slice(0, 3).map(doc => `• ${doc.name} - ${doc.keyContent[0]}`).join('\n')}

What specific help do you need?`;
      }

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: response,
        timestamp: new Date().toISOString()
      };

      setChatMessages(prev => [...prev, assistantMessage]);
      setIsGenerating(false);
    }, 1500);

    setNewMessage('');
  };

  const SearchResultCard = ({ result }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2">
          {result.type === 'parameter' && <Gauge className="h-4 w-4 text-blue-600" />}
          {result.type === 'fault' && <AlertTriangle className="h-4 w-4 text-red-600" />}
          {result.type === 'standard' && <FileText className="h-4 w-4 text-green-600" />}
          {result.type === 'wiring' && <Cpu className="h-4 w-4 text-purple-600" />}
          {result.type === 'safety' && <Shield className="h-4 w-4 text-orange-600" />}
          <h3 className="font-medium text-gray-900">{result.title}</h3>
        </div>
        {result.relevance && (
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
            {result.relevance}% match
          </span>
        )}
      </div>
      
      <p className="text-sm text-gray-600 mb-2">{result.content}</p>
      
      {result.example && (
        <div className="bg-gray-50 p-2 rounded text-xs font-mono mb-2">
          {result.example}
        </div>
      )}
      
      {result.solution && (
        <div className="bg-yellow-50 p-2 rounded text-xs mb-2">
          <strong>Solution:</strong> {result.solution}
        </div>
      )}
      
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{result.source}</span>
        {result.quickAction && (
          <button className="bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700">
            {result.quickAction}
          </button>
        )}
      </div>
    </div>
  );

  const renderSmartSearch = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Smart Documentation Search</h2>
        <div className="text-sm text-gray-500">
          {uploadedDocs.length} documents processed • {uploadedDocs.reduce((acc, doc) => acc + (doc.pages || 0), 0)} pages indexed
        </div>
      </div>

      {/* Enhanced Search Bar */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="relative mb-4">
          <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Ask anything about your documentation... (e.g., 'parameter 2.01', 'fault F042', 'naming convention')"
            className="pl-10 pr-4 py-3 border border-gray-300 rounded-lg w-full text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          {['Parameter 2.01', 'Fault F042', 'Naming convention', 'Motor wiring', 'Safety SIL 2'].map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => setSearchQuery(suggestion)}
              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>

      {/* Search Results */}
      <div className="space-y-4">
        {getSearchResults(searchQuery).map((result, index) => (
          <SearchResultCard key={index} result={result} />
        ))}
      </div>

      {/* Document Library */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Your Documentation Library</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {uploadedDocs.map((doc) => (
              <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-center space-x-2 mb-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-sm">{doc.name}</span>
                </div>
                <p className="text-xs text-gray-500 mb-2">{doc.size} • {doc.pages} pages</p>
                <div className="space-y-1">
                  {doc.keyContent.slice(0, 2).map((content, i) => (
                    <p key={i} className="text-xs text-gray-600">• {content}</p>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    doc.processed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {doc.processed ? 'Processed' : 'Processing'}
                  </span>
                  <button className="text-blue-600 hover:text-blue-800 text-xs">
                    Search within
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderDocChat = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Documentation Assistant</h2>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
          <span>AI ready • {uploadedDocs.length} docs loaded</span>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border flex flex-col h-96">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
            <Bot className="h-5 w-5 text-blue-600" />
            <span>Ask about your documentation</span>
          </h3>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {chatMessages.map((message) => (
            <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-3xl px-4 py-3 rounded-lg ${
                message.type === 'user' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-900'
              }`}>
                <div className="text-sm whitespace-pre-line">{message.content}</div>
                <p className={`text-xs mt-2 ${
                  message.type === 'user' ? 'text-blue-200' : 'text-gray-500'
                }`}>
                  {new Date(message.timestamp).toLocaleTimeString()}
                </p>
              </div>
            </div>
          ))}
          {isGenerating && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-900 px-4 py-3 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">Searching your documentation...</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Ask about parameters, fault codes, naming conventions, wiring..."
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              onClick={handleSendMessage}
              disabled={isGenerating}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          
          <div className="flex flex-wrap gap-2 mt-2">
            {['What\'s parameter 2.01?', 'How to fix fault F042?', 'Generate motor control', 'Show naming rules'].map((example) => (
              <button
                key={example}
                onClick={() => setNewMessage(example)}
                className="text-xs text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded"
              >
                "{example}"
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderQuickLookup = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Quick Reference Lookup</h2>
        <div className="text-sm text-gray-500">
          Instant answers from your documentation
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* VFD Parameters */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Gauge className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">VFD Parameters</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="border-b border-gray-100 pb-2">
              <div className="flex justify-between">
                <span className="font-medium">2.01</span>
                <span className="text-gray-600">Acceleration Time</span>
              </div>
              <div className="text-xs text-gray-500">0.1-999.9s, Default: 10.0s</div>
            </div>
            <div className="border-b border-gray-100 pb-2">
              <div className="flex justify-between">
                <span className="font-medium">2.02</span>
                <span className="text-gray-600">Deceleration Time</span>
              </div>
              <div className="text-xs text-gray-500">0.1-999.9s, Default: 15.0s</div>
            </div>
            <div className="border-b border-gray-100 pb-2">
              <div className="flex justify-between">
                <span className="font-medium">1.01</span>
                <span className="text-gray-600">Motor Nominal Current</span>
              </div>
              <div className="text-xs text-gray-500">Motor FLA setting</div>
            </div>
          </div>
          <button className="w-full mt-4 text-sm bg-blue-100 text-blue-700 py-2 rounded hover:bg-blue-200">
            View All 2,847 Parameters
          </button>
        </div>

        {/* Fault Codes */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center space-x-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <h3 className="text-lg font-medium text-gray-900">Common Fault Codes</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="border-b border-gray-100 pb-2">
              <div className="flex justify-between">
                <span className="font-medium text-red-600">F042</span>
                <span className="text-gray-600">Communication Error</span>
              </div>
              <div className="text-xs text-gray-500">Check network connection</div>
            </div>
            <div className="border-b border-gray-100 pb-2">
              <div className="flex justify-between">
                <span className="font-medium text-red-600">F001</span>
                <span className="text-gray-600">Overcurrent</span>
              </div>
              <div className="text-xs text-gray-500">Motor overload detected</div>
            </div>
            <div className="border-b border-gray-100 pb-2">
              <div className="flex justify-between">
                <span className="font-medium text-orange-600">F023</span>
                <span className="text-gray-600">Overtemperature</span>
              </div>
              <div className="text-xs text-gray-500">Drive thermal protection</div>
            </div>
          </div>
          <button className="w-full mt-4 text-sm bg-red-100 text-red-700 py-2 rounded hover:bg-red-200">
            View All Fault Codes
          </button>
        </div>

        {/* Naming Standards */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Tag className="h-5 w-5 text-green-600" />
            <h3 className="text-lg font-medium text-gray-900">Naming Standards</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="border-b border-gray-100 pb-2">
              <div className="flex justify-between">
                <span className="font-medium">i</span>
                <span className="text-gray-600">PLC Inputs</span>
              </div>
              <div className="text-xs text-gray-500 font-mono">iConv01_StartPB</div>
            </div>
            <div className="border-b border-gray-100 pb-2">
              <div className="flex justify-between">
                <span className="font-medium">q</span>
                <span className="text-gray-600">PLC Outputs</span>
              </div>
              <div className="text-xs text-gray-500 font-mono">qMotor01_Run</div>
            </div>
            <div className="border-b border-gray-100 pb-2">
              <div className="flex justify-between">
                <span className="font-medium">m</span>
                <span className="text-gray-600">Markers/Flags</span>
              </div>
              <div className="text-xs text-gray-500 font-mono">mSystem_Ready</div>
            </div>
          </div>
          <button className="w-full mt-4 text-sm bg-green-100 text-green-700 py-2 rounded hover:bg-green-200">
            View All Standards
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Code className="h-6 w-6 text-blue-600 mb-2" />
            <span className="text-sm font-medium">Generate Code</span>
            <span className="text-xs text-gray-500">Using your standards</span>
          </button>
          <button className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Search className="h-6 w-6 text-green-600 mb-2" />
            <span className="text-sm font-medium">Find Parameter</span>
            <span className="text-xs text-gray-500">From any manual</span>
          </button>
          <button className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
            <Wrench className="h-6 w-6 text-orange-600 mb-2" />
            <span className="text-sm font-medium">Troubleshoot</span>
            <span className="text-xs text-gray-500">Fault code lookup</span>
          </button>
          <button className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
            <FileText className="h-6 w-6 text-purple-600 mb-2" />
            <span className="text-sm font-medium">Standards</span>
            <span className="text-xs text-gray-500">Check conventions</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderCodeGeneration = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Documentation-Based Code Generator</h2>
        <div className="text-sm text-gray-500">
          Generates code using your uploaded standards
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Code Generation Form */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Generate Custom Code</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">What do you want to create?</label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-3 py-2 h-20 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Motor control with VFD, Safety interlock system, Conveyor with sensors..."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Equipment Type:</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option>Motor/VFD</option>
                  <option>Conveyor System</option>
                  <option>Safety System</option>
                  <option>Analog Processing</option>
                  <option>Communication</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Standards to Apply:</label>
                <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option>SIT Standards 2018</option>
                  <option>IEC 61131-3</option>
                  <option>Safety SIL 2</option>
                  <option>All Standards</option>
                </select>
              </div>
            </div>
            
            <button 
              onClick={() => {
                setGeneratedCode(generateCodeFromDocs('motor control with VFD'));
                setShowCodeModal(true);
              }}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
            >
              <Zap className="h-5 w-5" />
              <span>Generate Code Using My Documentation</span>
            </button>
          </div>
        </div>

        {/* Applied Standards Preview */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Applied Standards & Rules</h3>
          
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-800">SIT Standards 2018</span>
              </div>
              <div className="text-sm text-green-700 space-y-1">
                <p>• Variable prefixes: i (inputs), q (outputs), m (markers)</p>
                <p>• UpperCamelCase naming convention</p>
                <p>• Equipment mark format: e1021_19_XX</p>
                <p>• Customer abbreviation: Ama (Amazon)</p>
              </div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-800">ABB ACS580 Integration</span>
              </div>
              <div className="text-sm text-blue-700 space-y-1">
                <p>• Parameter references (2.01, 2.02, etc.)</p>
                <p>• Fault code handling (F042, F001)</p>
                <p>• Communication protocols</p>
                <p>• Commissioning procedures</p>
              </div>
            </div>
            
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="h-4 w-4 text-orange-600" />
                <span className="font-medium text-orange-800">Safety Requirements</span>
              </div>
              <div className="text-sm text-orange-700 space-y-1">
                <p>• SIL 2 compliance per uploaded specs</p>
                <p>• Dual channel architecture</p>
                <p>• Emergency stop integration</p>
                <p>• Response time requirements</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Generations */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Code Generations</h3>
        <div className="space-y-3">
          {[
            { name: 'Motor Control with ABB VFD', date: '2 hours ago', standards: 'SIT + ACS580', size: '247 lines' },
            { name: 'Safety Interlock System', date: 'Yesterday', standards: 'SIT + SIL 2', size: '184 lines' },
            { name: 'Conveyor Sequence Logic', date: '3 days ago', standards: 'SIT + Project Specs', size: '312 lines' }
          ].map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
              <div>
                <span className="font-medium text-gray-900">{item.name}</span>
                <div className="text-sm text-gray-500">{item.date} • {item.standards} • {item.size}</div>
              </div>
              <div className="flex space-x-2">
                <button className="text-blue-600 hover:text-blue-800 text-sm">View</button>
                <button className="text-gray-600 hover:text-gray-800 text-sm">Copy</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Code Modal */}
      {showCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Generated Code (Following Your Standards)</h3>
              <div className="flex space-x-2">
                <button className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm flex items-center space-x-1">
                  <Download className="h-4 w-4" />
                  <span>Export</span>
                </button>
                <button className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm flex items-center space-x-1">
                  <Copy className="h-4 w-4" />
                  <span>Copy</span>
                </button>
                <button 
                  onClick={() => setShowCodeModal(false)}
                  className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono whitespace-pre-wrap leading-relaxed">
                {generatedCode}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const sidebarItems = [
    { id: 'smart-search', label: 'Smart Search', icon: Search, description: 'Search across all documentation' },
    { id: 'doc-chat', label: 'Doc Assistant', icon: MessageCircle, description: 'Chat with your documents' },
    { id: 'quick-lookup', label: 'Quick Lookup', icon: Zap, description: 'Instant parameter/fault lookup' },
    { id: 'code-gen', label: 'Code Generator', icon: Code, description: 'Generate code using standards' },
    { id: 'settings', label: 'Settings', icon: Settings, description: 'Platform configuration' }
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 bg-white shadow-sm border-r border-gray-200 flex flex-col">
        <div className="p-6">
          <div className="flex items-center space-x-2">
            <Bot className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Galo PLC Assistant</h1>
              <p className="text-xs text-gray-500">Documentation-Driven Programming</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center px-6 py-3 text-left hover:bg-gray-50 transition-colors ${
                activeTab === item.id ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' : 'text-gray-600'
              }`}
            >
              <item.icon className="h-5 w-5 mr-3" />
              <div>
                <div className="font-medium">{item.label}</div>
                <div className="text-xs text-gray-500">{item.description}</div>
              </div>
            </button>
          ))}
        </nav>

        {/* Documentation Status */}
        <div className="p-6 border-t border-gray-200">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Documents</span>
              <span className="font-medium text-blue-600">{uploadedDocs.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Pages Indexed</span>
              <span className="font-medium text-green-600">{uploadedDocs.reduce((acc, doc) => acc + (doc.pages || 0), 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">AI Status</span>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="font-medium text-green-600">Ready</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <h2 className="text-2xl font-semibold text-gray-900 capitalize">
                {activeTab.replace('-', ' ')}
              </h2>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>Documentation processed & ready</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <select 
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="current">Current Project</option>
                <option value="library">Library Standards</option>
                <option value="all">All Documents</option>
              </select>
              
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                  PE
                </div>
                <span className="text-sm font-medium text-gray-900">PLC Engineer</span>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-6">
          {activeTab === 'smart-search' && renderSmartSearch()}
          {activeTab === 'doc-chat' && renderDocChat()}
          {activeTab === 'quick-lookup' && renderQuickLookup()}
          {activeTab === 'code-gen' && renderCodeGeneration()}
          {activeTab === 'settings' && (
            <div className="text-center py-12">
              <Settings className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Platform Settings</h3>
              <p className="text-gray-500">Document processing, AI preferences, and integration settings</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DocumentDrivenPLCPlatform;